import { Home, User, BarChart3, LogOut, Shield, FileText, BookOpen, Clock, Youtube, Globe, Bell, Share2, History, Library, Package, Brain, FileDown } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useNavigate } from 'react-router-dom';
import { fetchMyProfile } from '@/lib/supabaseHelpers';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminRole } from '@/hooks/useAdminRole';
import { SIDEBAR_ITEMS, resolveNav, useUiConfig } from '@/lib/uiConfig';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DarkModeToggle } from '@/components/DarkModeToggle';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';



export function AppSidebar() {
  const { state, setOpenMobile, isMobile } = useSidebar();
  const collapsed = !isMobile && state === 'collapsed';
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminRole();
  
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [availableTests, setAvailableTests] = useState(0);
  const [testsAttempted, setTestsAttempted] = useState(0);
  const [totalTestsTaken, setTotalTestsTaken] = useState(0);
  const [avgAccuracy, setAvgAccuracy] = useState('—');

  useEffect(() => {
    if (!user) return;

    // Fetch profile
    fetchMyProfile(user.id).then((data) => {
      if (data) {
        setAvatarUrl(data.avatar_url);
        setDisplayName(data.display_name || data.full_name || '');
      }
    });

    // Fetch stats via lightweight RPC
    (supabase.rpc as any)('get_user_stats', { _user_id: user.id })
      .maybeSingle()
      .then(({ data }: any) => {
        if (data) {
          setAvailableTests(data.available_tests ?? 0);
          setTestsAttempted(data.tests_attempted ?? 0);
          setTotalTestsTaken(data.total_tests_taken ?? 0);
          setAvgAccuracy(data.avg_accuracy > 0 ? `${data.avg_accuracy}%` : '—');
        }
      });
  }, [user]);

  const ui = useUiConfig();
  const allItems = [
    ...resolveNav(ui, SIDEBAR_ITEMS),
    ...(isAdmin ? [{ key: 'admin', title: 'Admin', url: '/admin', Icon: Shield, external: false }] : []),
  ];


  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleShare = async () => {
    const shareUrl = 'https://www.indurstudycircle.com';
    const shareData = {
      title: 'INDUR STUDY CIRCLE',
      text: 'Join INDUR STUDY CIRCLE — Mock tests for Telangana government exams. Open the app:',
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard');
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied to clipboard');
        } catch {
          toast.error('Unable to share');
        }
      }
    }
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        {/* User info */}
        {!collapsed && (
          <button
            onClick={() => {
              navigate('/profile');
              if (isMobile) setOpenMobile(false);
            }}
            className="w-full px-4 py-4 border-b border-border hover:bg-muted/50 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <Avatar className="w-9 h-9">
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback className="text-xs font-bold bg-primary text-primary-foreground">
                  {(displayName || user?.email || 'U')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">
                  {displayName || 'User'}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </button>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {allItems.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton asChild>
                    {item.external ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center hover:bg-muted/50"
                        onClick={() => { if (isMobile) setOpenMobile(false); }}
                      >
                        <item.Icon className="mr-2 h-4 w-4" />
                        {!collapsed && <span>{item.title}</span>}
                      </a>
                    ) : (
                      <NavLink
                        to={item.url}
                        end={item.url === '/'}
                        className="hover:bg-muted/50"
                        activeClassName="bg-muted text-primary font-medium"
                        onClick={() => { if (isMobile) setOpenMobile(false); }}
                      >
                        <item.Icon className="mr-2 h-4 w-4" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>


        {/* Stats */}
        {!collapsed && (
          <SidebarGroup>
            <SidebarGroupLabel>My Stats</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="grid grid-cols-2 gap-2 px-2">
                {[
                  { label: 'Available Tests', value: availableTests, icon: FileText },
                  { label: 'Tests Attempted', value: testsAttempted, icon: BookOpen },
                  { label: 'Tests Taken', value: totalTestsTaken, icon: BarChart3 },
                  { label: 'Avg. Accuracy', value: avgAccuracy, icon: Clock },
                ].map(stat => (
                  <div key={stat.label} className="bg-secondary/50 rounded-lg p-2.5 flex flex-col items-center text-center gap-1">
                    <div className="w-7 h-7 rounded-md bg-background flex items-center justify-center">
                      <stat.icon className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <div className="text-sm font-semibold font-mono-tabular text-foreground">{stat.value}</div>
                    <div className="text-[9px] text-muted-foreground leading-tight">{stat.label}</div>
                  </div>
                ))}
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <div className="flex flex-col gap-1 px-2 py-2">
          {!collapsed ? (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-primary hover:text-primary hover:bg-primary/10 text-xs"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4 mr-2" /> Share App
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="text-primary hover:text-primary hover:bg-primary/10"
              onClick={handleShare}
              title="Share App"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          )}
          {!collapsed && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10 text-xs"
              onClick={handleSignOut}
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          )}
          {collapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleSignOut}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
